import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Product } from '../types/product';

interface ProductContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  fetchProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

// Mock products data
const MOCK_PRODUCTS: Product[] = [
  {
    id: 1,
    name: 'Sample Product 1',
    description: 'This is a sample product description.',
    price: 99.99,
    image: 'https://via.placeholder.com/300',
    category: 'Electronics' as const,
    stock: 10,
    isInStock: true,
  },
  {
    id: 2,
    name: 'Sample Product 2',
    description: 'Another sample product description.',
    price: 149.99,
    image: 'https://via.placeholder.com/300',
    category: 'Clothing' as const,
    stock: 5,
    isInStock: true,
  },
];

export const ProductProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setProducts(MOCK_PRODUCTS);
      setError(null);
    } catch (err) {
      setError('Failed to fetch products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <ProductContext.Provider value={{ products, loading, error, fetchProducts }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};
